import { useCallback, useEffect, useRef, useState } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { Text } from "@/components/Typography";
import Ellipses, { SINGLE_ELLIPSES_CYCLE } from "./Ellipses";

interface ThinkingAnimationProps {
  isVisible: boolean;
  onComplete?: () => void;
}

const THINKING_TEXTS = [
  "Thinking",
  "Still thinking",
  "Deep in thought",
  "Analyzing the details",
  "Piecing it together",
];

const INITIAL_DELAY = 500;
const CYCLE_DURATION = 4 * SINGLE_ELLIPSES_CYCLE; // 4 complete ellipses cycles
const TRANSITION_DURATION = 300;
const FADE_OUT_DURATION = 200;

const ThinkingAnimation = ({
  isVisible,
  onComplete,
}: ThinkingAnimationProps) => {
  const reducedMotion = useReducedMotion();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isStarted, setIsStarted] = useState(false);

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimers = useCallback(() => {
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];
  }, []);

  const transitionToNextText = useCallback(
    (nextIndex: number) => {
      if (reducedMotion) {
        setCurrentTextIndex(nextIndex);
        return;
      }

      // Push up and fade out
      translateY.value = withTiming(-20, {
        duration: TRANSITION_DURATION / 2,
        easing: Easing.out(Easing.quad),
      });

      opacity.value = withTiming(0, {
        duration: TRANSITION_DURATION / 2,
        easing: Easing.out(Easing.quad),
      });

      const transitionTimeout = setTimeout(() => {
        setCurrentTextIndex(nextIndex);

        // Fade in
        translateY.value = 15;
        translateY.value = withTiming(0, {
          duration: TRANSITION_DURATION / 2,
          easing: Easing.out(Easing.quad),
        });

        opacity.value = withTiming(1, {
          duration: TRANSITION_DURATION / 2,
          easing: Easing.out(Easing.quad),
        });
      }, TRANSITION_DURATION / 2);

      timeoutRefs.current.push(transitionTimeout);
    },
    [reducedMotion, translateY, opacity],
  );

  useEffect(() => {
    if (!isVisible) {
      clearAllTimers();
      setIsStarted(false);
      setCurrentTextIndex(0);
      opacity.value = 0;
      translateY.value = 0;
      return;
    }

    if (reducedMotion) {
      setIsStarted(true);
      setCurrentTextIndex(0);
      opacity.value = 1;
      return;
    }

    const startTimeout = setTimeout(() => {
      setIsStarted(true);

      opacity.value = withTiming(1, {
        duration: TRANSITION_DURATION,
        easing: Easing.out(Easing.quad),
      });

      for (let i = 1; i < THINKING_TEXTS.length; i++) {
        const transitionTimeout = setTimeout(() => {
          transitionToNextText(i);
        }, CYCLE_DURATION * i);
        timeoutRefs.current.push(transitionTimeout);
      }
    }, INITIAL_DELAY);

    timeoutRefs.current.push(startTimeout);

    return clearAllTimers;
  }, [
    isVisible,
    reducedMotion,
    opacity,
    translateY,
    clearAllTimers,
    transitionToNextText,
  ]);

  useEffect(() => {
    if (!isVisible && isStarted) {
      if (reducedMotion) {
        onComplete?.();
        return;
      }

      translateY.value = withTiming(-20, {
        duration: FADE_OUT_DURATION,
        easing: Easing.out(Easing.quad),
      });

      opacity.value = withTiming(0, {
        duration: FADE_OUT_DURATION,
        easing: Easing.out(Easing.quad),
      });

      const completeTimeout = setTimeout(() => {
        onComplete?.();
      }, FADE_OUT_DURATION);

      timeoutRefs.current.push(completeTimeout);
    }
  }, [isVisible, isStarted, opacity, translateY, onComplete, reducedMotion]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    }),
    [opacity, translateY],
  );

  if (!isStarted) return null;

  const currentText = THINKING_TEXTS[currentTextIndex];

  if (reducedMotion) {
    return (
      <Text color="$textSecondary" fontFamily="$serif" flexDirection="column">
        {currentText}
        <Ellipses />
      </Text>
    );
  }

  return (
    <Animated.View style={animatedStyle}>
      <Text color="$textSecondary" fontFamily="$serif">
        {currentText}
        <Ellipses />
      </Text>
    </Animated.View>
  );
};

export default ThinkingAnimation;
